import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, fieldErrors, unwrap } from '../../api/client';
import type { AssetDetail, AssetStatus } from '../../api/types';
import { EmployeeSelect, LocationSelect } from '../../components/pickers';
import { Button, ConfirmDialog, DateInput, Dialog, ErrorBox, Field, Notice, Select, Textarea, useToast } from '../../components/ui';
import { STATUS_LABELS, STATUS_TRANSITIONS, todayIso } from '../../lib/format';

type Props = { asset: AssetDetail; open: boolean; onClose: () => void };

/** Nach jeder Änderung am Asset: Detail, Listen und Historie neu laden. */
export const useAssetInvalidate = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['asset'] });
    void qc.invalidateQueries({ queryKey: ['assets'] });
    void qc.invalidateQueries({ queryKey: ['employees'] });
    void qc.invalidateQueries({ queryKey: ['locations'] });
  };
};

function DateNote({
  date,
  setDate,
  note,
  setNote,
  noteLabel = 'Notiz',
  errors,
}: {
  date: string;
  setDate: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  noteLabel?: string;
  errors: Record<string, string>;
}) {
  return (
    <>
      <Field label="Gültig ab" hint="Fachliches Datum, z. B. Tag der Übergabe" error={errors.effective_date}>
        {(p) => <DateInput {...p} max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />}
      </Field>
      <Field label={noteLabel} error={errors.note}>
        {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
    </>
  );
}

export function AssignDialog({ asset, open, onClose }: Props) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const [employee, setEmployee] = useState('');
  const [status, setStatus] = useState<string>('');
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setEmployee(asset.employee?.short_code ?? '');
      setStatus('');
      setDate(todayIso());
      setNote('');
    }
  }, [open, asset]);

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/assets/{ref}/assign', {
          params: { path: { ref: asset.id } },
          body: {
            employee: employee || null,
            status: (status || undefined) as Exclude<AssetStatus, 'disposed'> | undefined,
            effective_date: date || undefined,
            note: note.trim() || null,
            version: asset.version,
          },
        }),
      ),
    onSuccess: () => {
      invalidate();
      toast(employee ? `Zugeordnet an ${employee}` : 'Rückgabe erfasst');
      onClose();
    },
  });

  const isReturn = !employee && !!asset.employee;
  const allowed = STATUS_TRANSITIONS[asset.status];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Mitarbeiter zuordnen"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!employee && !asset.employee}>
            {isReturn ? 'Rückgabe speichern' : 'Zuordnen'}
          </Button>
        </>
      }
    >
      <Field label="Mitarbeiter" hint="Leer = Rückgabe (niemandem zugeordnet)" error={fieldErrors(m.error).employee}>
        {(p) => <EmployeeSelect {...p} value={employee} onChange={setEmployee} emptyLabel="– niemand (Rückgabe) –" />}
      </Field>
      <Field label="Status" hint={asset.status === 'in_stock' && employee ? 'Standard: wird „Aktiv“' : 'Standard: unverändert'}>
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">– automatisch –</option>
            {allowed
              .filter((s) => s !== 'disposed')
              .map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
          </Select>
        )}
      </Field>
      <DateNote date={date} setDate={setDate} note={note} setNote={setNote} errors={fieldErrors(m.error)} />
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

export function LocationDialog({ asset, open, onClose }: Props) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const [location, setLocation] = useState('');
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setLocation(asset.location?.id ?? '');
      setDate(todayIso());
      setNote('');
    }
  }, [open, asset]);

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/assets/{ref}/location', {
          params: { path: { ref: asset.id } },
          body: { location: location || null, effective_date: date || undefined, note: note.trim() || null, version: asset.version },
        }),
      ),
    onSuccess: () => {
      invalidate();
      toast('Standort gespeichert');
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Standort ändern"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <Field label="Standort" hint="Leer = unbekannt">
        {(p) => <LocationSelect {...p} value={location} onChange={setLocation} emptyLabel="– unbekannt –" />}
      </Field>
      <DateNote date={date} setDate={setDate} note={note} setNote={setNote} errors={fieldErrors(m.error)} />
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

export function StatusDialog({ asset, open, onClose }: Props) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const allowed = STATUS_TRANSITIONS[asset.status];
  const [status, setStatus] = useState<string>('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayIso());
  useEffect(() => {
    if (open) {
      setStatus(allowed[0] ?? '');
      setReason('');
      setDate(todayIso());
    }
  }, [open, asset.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/assets/{ref}/status', {
          params: { path: { ref: asset.id } },
          body: { status: status as AssetStatus, reason: reason.trim(), effective_date: date || undefined, version: asset.version },
        }),
      ),
    onSuccess: () => {
      invalidate();
      toast(`Status: ${STATUS_LABELS[status as AssetStatus]}`);
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Status ändern"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!status || !reason.trim()}>
            Speichern
          </Button>
        </>
      }
    >
      <Notice>
        Aktuell: <strong>{STATUS_LABELS[asset.status]}</strong>. Verschrotten geht über „Verschrotten“ (mit Protokoll).
      </Notice>
      <Field label="Neuer Status" required>
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
            {allowed.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Begründung" required error={fieldErrors(m.error).reason}>
        {(p) => <Textarea {...p} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Display defekt, eingeschickt" />}
      </Field>
      <Field label="Gültig ab">{(p) => <DateInput {...p} max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

export function DeleteAssetDialog({ asset, open, onClose }: Props) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const navigate = useNavigate();
  const m = useMutation({
    mutationFn: (reason: string) => unwrap(api.DELETE('/api/assets/{ref}', { params: { path: { ref: asset.id } }, body: { reason, version: asset.version } })),
    onSuccess: () => {
      invalidate();
      toast(`${asset.asset_number} als Fehlerfassung gelöscht`);
      navigate('/assets');
    },
  });
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={`${asset.asset_number} löschen`}
      message={
        <>
          Nur für <strong>Fehlerfassungen</strong> (z. B. doppelt angelegt). Das Asset wird ausgeblendet, bleibt aber mit Historie erhalten.
          Ausgemusterte Geräte bitte <strong>verschrotten</strong> statt löschen.
        </>
      }
      danger
      requireReason
      confirmLabel="Löschen"
      loading={m.isPending}
      error={m.error}
      onConfirm={(r) => m.mutate(r)}
    />
  );
}
